import type { RowV5ResponseBody } from '../types/api/data';

/**
 * Module-level cache for page row data.
 * Survives component re-renders so previously fetched pages are returned instantly
 * when navigating back (e.g. page 1 → page 2 → page 1).
 * Keyed by `gridCode:queryId:offset:limit:columns`. Capped at 50 entries — oldest evicted first.
 */
const PAGE_DATA_CACHE_MAX_SIZE = 50;
const pageDataCache = new Map<string, RowV5ResponseBody[]>();

/** @internal Clear the module-level cache. Used only by test suites. */
export function _clearPageDataCache(): void {
  pageDataCache.clear();
}

/** Removes all cached entries whose key starts with `gridCode:`. */
export function clearPageDataCacheForGrid(gridCode: string): void {
  const prefix = `${gridCode}:`;
  for (const key of pageDataCache.keys()) {
    if (key.startsWith(prefix)) {
      pageDataCache.delete(key);
    }
  }
}

/**
 * Builds a composite cache key from grid, query, pagination, and column parameters.
 * Format: `gridCode:queryId:offset:limit:col1,col2,...`
 */
export function getPageDataCacheKey(gridCode: string, queryId: string, offset: number, limit: number, columns: string[]): string {
  return `${gridCode}:${queryId}:${offset}:${limit}:${columns.join(',')}`;
}

/** Returns cached row data for the given page key, or `undefined` on a cache miss. */
export function getCachedPageData(key: string): RowV5ResponseBody[] | undefined {
  return pageDataCache.get(key);
}

/**
 * Stores row data in the page cache with LRU eviction.
 * If the cache exceeds {@link PAGE_DATA_CACHE_MAX_SIZE} entries, the oldest entry is evicted.
 */
export function setCachedPageData(key: string, data: RowV5ResponseBody[]): void {
  pageDataCache.delete(key); // re-insert at end (Map preserves insertion order)
  pageDataCache.set(key, data);
  if (pageDataCache.size > PAGE_DATA_CACHE_MAX_SIZE) {
    const oldestKey = pageDataCache.keys().next().value;
    if (oldestKey !== undefined) {
      pageDataCache.delete(oldestKey);
    }
  }
}